import { Fragment, type PropsWithChildren } from "react";
import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { tv } from "tailwind-variants";

const sidebarVariants = tv({
  slots: {
    root: "relative z-20",
    overlay: "fixed inset-0 bg-black/60 backdrop-blur-sm",
    panel:
      "fixed inset-y-0 right-0 h-full w-full overflow-y-auto border-l border-line bg-surface shadow-overlay sm:w-120",
    overlayEnter: "transition-opacity duration-300",
    overlayEnterFrom: "opacity-0",
    overlayEnterTo: "opacity-100",
    overlayLeave: "transition-opacity duration-300",
    overlayLeaveFrom: "opacity-100",
    overlayLeaveTo: "opacity-0",
    panelEnter: "transition-transform duration-300 ease-in-out",
    panelEnterFrom: "translate-x-full",
    panelEnterTo: "translate-x-0",
    panelLeave: "transition-transform duration-300 ease-in-out",
    panelLeaveFrom: "translate-x-0",
    panelLeaveTo: "translate-x-full",
  },
});

type SidebarProps = PropsWithChildren & {
  show: boolean;
  onClose: () => void;
};

export const Sidebar = ({ children, onClose, show }: SidebarProps) => {
  const styles = sidebarVariants();

  const overlayTransition = {
    enter: styles.overlayEnter(),
    enterFrom: styles.overlayEnterFrom(),
    enterTo: styles.overlayEnterTo(),
    leave: styles.overlayLeave(),
    leaveFrom: styles.overlayLeaveFrom(),
    leaveTo: styles.overlayLeaveTo(),
  };

  const panelTransition = {
    enter: styles.panelEnter(),
    enterFrom: styles.panelEnterFrom(),
    enterTo: styles.panelEnterTo(),
    leave: styles.panelLeave(),
    leaveFrom: styles.panelLeaveFrom(),
    leaveTo: styles.panelLeaveTo(),
  };

  return (
    <Transition as={Fragment} show={show} unmount={false} appear>
      <Dialog as="div" className={styles.root()} onClose={onClose}>
        <TransitionChild as={Fragment} {...overlayTransition} unmount={false}>
          <div className={styles.overlay()} />
        </TransitionChild>

        <TransitionChild as={Fragment} {...panelTransition} unmount={false}>
          <DialogPanel className={styles.panel()}>{children}</DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};
